import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Heart } from "lucide-react";
import { useStore } from "../../context/StoreContext";
import { LOGO_DARK, LOGO_LIGHT } from "../../data/products";

const HERO_ROUTES = ["/", "/exclusives", "/about"];

const Topbar = () => {
  const { menuOpen, setMenuOpen, cartCount, currency, setCurrency } = useStore();
  const location = useLocation();
  const isHero = HERO_ROUTES.includes(location.pathname);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = !isHero || scrolled || menuOpen;

  return (
    <header
      data-testid="topbar"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        solid
          ? "bg-cream/85 backdrop-blur-xl shadow-soft"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-8 py-3 max-w-[1440px] mx-auto">
        <Link to="/" className="flex items-center" data-testid="topbar-logo">
          <img
            src={solid ? LOGO_DARK : LOGO_LIGHT}
            alt="Damage Culture"
            className={`block w-auto transition-all duration-300 ${
              solid ? "h-9 md:h-11" : "h-11 md:h-14"
            }`}
            style={{ filter: solid ? "none" : "drop-shadow(0 2px 8px rgba(0,0,0,0.55))" }}
          />
        </Link>

        <div className="flex items-center gap-1.5 md:gap-2">
          <button
            onClick={() => setCurrency(currency === "INR" ? "USD" : "INR")}
            data-testid="currency-toggle"
            className={`font-mono text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] px-3 sm:px-3.5 py-2 rounded-full transition-colors ${
              solid
                ? "bg-ink/5 text-ink hover:bg-ink hover:text-cream"
                : "bg-cream/15 backdrop-blur text-cream hover:bg-cream hover:text-ink"
            }`}
            aria-label="Toggle currency"
          >
            <span className="sm:hidden">{currency === "INR" ? "\u20B9" : "$"}</span>
            <span className="hidden sm:inline">{currency === "INR" ? "INR \u20B9" : "USD $"}</span>
          </button>

          <Link
            to="/wishlist"
            data-testid="topbar-wishlist"
            aria-label="Wishlist"
            className={`p-2.5 rounded-full transition-colors ${
              solid
                ? "bg-ink/5 text-ink hover:bg-ink hover:text-cream"
                : "bg-cream/15 backdrop-blur text-cream hover:bg-cream hover:text-ink"
            }`}
          >
            <Heart size={18} strokeWidth={1.75} />
          </Link>

          <Link
            to="/cart"
            data-testid="topbar-cart"
            aria-label="Cart"
            className={`relative p-2.5 rounded-full transition-colors ${
              solid
                ? "bg-ink/5 text-ink hover:bg-ink hover:text-cream"
                : "bg-cream/15 backdrop-blur text-cream hover:bg-cream hover:text-ink"
            }`}
          >
            <ShoppingBag size={18} strokeWidth={1.75} />
            {cartCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-dc-red text-cream text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center shadow-float"
              >
                {cartCount}
              </span>
            )}
          </Link>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="menu-toggle"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className={`p-2.5 rounded-full transition-colors ${
              solid || menuOpen
                ? "bg-ink text-cream shadow-float"
                : "bg-ink/80 backdrop-blur text-cream"
            }`}
          >
            {menuOpen ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
